import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const MyApplicationsPage = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.myApplications, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load applications');
        setLoading(false);
        return;
      }

      // Transform backend data to frontend format
      const transformedApplications = data.applications.map((app: any) => ({
        id: app.id,
        manuscript: {
          id: app.manuscript.id,
          title: app.manuscript.title,
          author: app.manuscript.author?.displayName || 'Unknown Author',
          genre: formatGenre(app.manuscript.primaryGenre),
          wordCount: app.manuscript.wordCount,
        },
        status: app.status.toLowerCase(),
        appliedDate: new Date(app.createdAt).toISOString().split('T')[0],
        message: app.whyInterested,
      }));

      setApplications(transformedApplications);
      setLoading(false);
    } catch (err) {
      console.error('Fetch applications error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Helper to format backend genre enum to frontend
  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  // Hardcoded mock data for reference (will be replaced by API data)
  const _unused_mockApplications = [
    {
      id: 1,
      manuscript: {
        id: 101,
        title: 'Beneath Stone Bridges',
        author: 'Sarah Chen',
        genre: 'Literary Fiction',
        wordCount: 78000,
      },
      status: 'pending',
      appliedDate: '2024-01-25',
      message: 'I\'m very interested in beta reading your literary fiction novel. I have experience beta reading in this genre and appreciate nuanced character development...',
    },
    {
      id: 2,
      manuscript: {
        id: 102,
        title: 'Starlight Protocol',
        author: 'James Rivera',
        genre: 'Science Fiction',
        wordCount: 110000,
      },
      status: 'pending',
      appliedDate: '2024-01-28',
      message: 'As a science fiction enthusiast and beta reader, I\'d love to help with your manuscript. I particularly enjoy hard sci-fi and space exploration stories...',
    },
    {
      id: 3,
      manuscript: {
        id: 103,
        title: 'The Last Garden',
        author: 'Maria Santos',
        genre: 'Fantasy',
        wordCount: 95000,
      },
      status: 'accepted',
      appliedDate: '2024-01-30',
      acceptedDate: '2024-02-01',
      message: 'Your fantasy novel sounds fascinating! I\'ve beta read several fantasy manuscripts and love ecological themes in worldbuilding...',
    },
    {
      id: 4,
      manuscript: {
        id: 104,
        title: 'City of Secrets',
        author: 'Lisa Anderson',
        genre: 'Mystery',
        wordCount: 79000,
      },
      status: 'declined',
      appliedDate: '2024-01-20',
      declinedDate: '2024-01-23',
      declinedReason: 'Thank you for your interest! I\'ve already filled my beta reader slots for this round.',
      message: 'I love mystery novels and would be excited to beta read your work. I pay special attention to plot pacing and clue placement...',
    },
    {
      id: 5,
      manuscript: {
        id: 105,
        title: 'Echoes of Tomorrow',
        author: 'David Park',
        genre: 'Thriller',
        wordCount: 85000,
      },
      status: 'withdrawn',
      appliedDate: '2024-01-15',
      withdrawnDate: '2024-01-18',
      message: 'I\'m interested in beta reading your thriller. However, I had to withdraw due to time constraints.',
    },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Applications' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'withdrawn', label: 'Withdrawn' },
  ];

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: 'primary' | 'success' | 'warning' | 'error' | 'default', text: string }> = {
      pending: { variant: 'warning', text: 'Pending' },
      accepted: { variant: 'success', text: 'Accepted' },
      declined: { variant: 'error', text: 'Declined' },
      withdrawn: { variant: 'default', text: 'Withdrawn' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const filteredApplications = applications.filter(app =>
    statusFilter === 'all' || app.status === statusFilter
  );

  const handleWithdraw = async (applicationId: string) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.withdraw(applicationId), {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to withdraw application');
        return;
      }

      window.alert('Application withdrawn successfully.');
      // Refresh applications list
      fetchApplications();
    } catch (err) {
      console.error('Withdraw application error:', err);
      window.alert('Network error. Please try again.');
    }
  };

  const handleViewManuscript = (manuscriptId: number) => {
    window.location.href = `/manuscripts/${manuscriptId}/preview`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            My Applications
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Track your beta reading applications
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading applications...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchApplications}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Filter Bar */}
        {!loading && !error && (
          <Card style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <Select
                  label="Filter by Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={statusOptions}
                />
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
              }}>
                {filteredApplications.length} application{filteredApplications.length !== 1 ? 's' : ''}
              </div>
            </div>
          </Card>
        )}

        {/* Applications List */}
        {!loading && !error && filteredApplications.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                {statusFilter === 'all'
                  ? 'You haven\'t applied to any manuscripts yet'
                  : `No ${statusFilter} applications`
                }
              </p>
              <Button onClick={() => window.location.href = '/manuscripts/discover'}>
                Discover Manuscripts
              </Button>
            </div>
          </Card>
        ) : !loading && !error ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {filteredApplications.map((application) => (
              <Card key={application.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '24px',
                }}>
                  <div style={{ flex: 1 }}>
                    {/* Title and Status */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                    }}>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        margin: 0,
                      }}>
                        {application.manuscript.title}
                      </h3>
                      {getStatusBadge(application.status)}
                    </div>

                    {/* Author and Genre */}
                    <p style={{
                      fontSize: '16px',
                      color: theme.colors.textSecondary,
                      marginBottom: '12px',
                    }}>
                      by {application.manuscript.author}
                    </p>

                    {/* Metadata */}
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                    }}>
                      <span>{application.manuscript.genre}</span>
                      <span>•</span>
                      <span>{application.manuscript.wordCount.toLocaleString()} words</span>
                      <span>•</span>
                      <span>Applied: {application.appliedDate}</span>
                    </div>

                    {/* Application Message Preview */}
                    <div style={{
                      padding: '12px',
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.small,
                      marginBottom: '16px',
                    }}>
                      <p style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        margin: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        <strong>Your message:</strong> {application.message}
                      </p>
                    </div>

                    {/* Status-specific information */}
                    {application.status === 'accepted' && application.acceptedDate && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.successLight,
                        borderRadius: theme.borderRadius.small,
                        marginBottom: '16px',
                      }}>
                        <p style={{
                          fontSize: '14px',
                          color: theme.colors.textPrimary,
                          margin: 0,
                        }}>
                          ✓ Accepted on {application.acceptedDate} - You can now start reading!
                        </p>
                      </div>
                    )}

                    {application.status === 'declined' && application.declinedReason && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        marginBottom: '16px',
                      }}>
                        <p style={{
                          fontSize: '14px',
                          color: theme.colors.textPrimary,
                          margin: 0,
                        }}>
                          <strong>Author's response:</strong> {application.declinedReason}
                        </p>
                      </div>
                    )}

                    {application.status === 'withdrawn' && application.withdrawnDate && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        marginBottom: '16px',
                      }}>
                        <p style={{
                          fontSize: '14px',
                          color: theme.colors.textSecondary,
                          margin: 0,
                        }}>
                          Withdrawn on {application.withdrawnDate}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}>
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => handleViewManuscript(application.manuscript.id)}
                      >
                        View Manuscript
                      </Button>

                      {application.status === 'accepted' && (
                        <Button
                          size="small"
                          onClick={() => window.location.href = `/reader/manuscripts/${application.manuscript.id}/read`}
                        >
                          Start Reading
                        </Button>
                      )}

                      {application.status === 'pending' && (
                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => handleWithdraw(application.id)}
                        >
                          Withdraw Application
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : null}

        {/* Help Card */}
        {!loading && !error && (
        <Card style={{
          backgroundColor: theme.colors.backgroundAlt,
          marginTop: '40px',
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Application Status Guide
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            fontSize: '14px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
          }}>
            <div>
              <strong style={{ color: theme.colors.warning }}>Pending:</strong> The author is reviewing your application
            </div>
            <div>
              <strong style={{ color: theme.colors.success }}>Accepted:</strong> You've been selected as a beta reader
            </div>
            <div>
              <strong style={{ color: theme.colors.error }}>Declined:</strong> The author selected other readers
            </div>
            <div>
              <strong style={{ color: theme.colors.textSecondary }}>Withdrawn:</strong> You withdrew your application
            </div>
          </div>
        </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};
